/* eslint-disable object-curly-newline */
/* global React */
import { useCallback } from 'react'
import { Button as MuiButton, useTheme } from '@mui/material'
import { isObj, PlatformEvent } from 'lib/util'
import { useMemoRef } from 'platform/react/hook'
import OfflineHOC from 'platform/react/hoc/offline'
import Icon from 'ui/Element/Icon'

/**
 * Retrieves the button variant which matches `size`.
 *
 * @param {Object} theme  the application theme
 * @param {string} size   the size to choose
 * @returns {{props: Partial<ComponentsPropsList["MuiButton"]>, style: Interpolation<{theme: Theme}>}|*}
 * @private
 */
const _getButtonVariant = (theme, size) => {
  const buttonVariants = theme.components.MuiButton.variants
  return buttonVariants.find(x => x.props.size === size)
}

/**
 *
 * Retrieves the `minHeight` prop of the button variant which matches `size`.
 *
 * @param {Object} theme  the application theme
 * @param {string} size   the size to choose
 * @returns {*|string}
 * @private
 */
const _getButtonSize = (theme, size) => _getButtonVariant(theme, size)?.style?.minHeight || '2.5rem'

/**
 *
 * Retrieves the `paddingTop` prop of the button variant which matches `size`.
 *
 * @param {Object} theme  the application theme
 * @param {string} size   the size to choose
 * @returns {*|string}
 * @private
 */
const _getButtonPadding = (theme, size) => _getButtonVariant(theme, size)?.style?.paddingTop || '1rem'

/**
 * Maps the name of the `size` prop to a value.
 * @type {{small: string, large: string, tiny: string, huge: string, medium: string}}
 */
const iconSizeMapping = {
  tiny: '1rem',
  small: '1.25rem',
  medium: '1.5rem',
  large: '1.5rem',
  huge: '1.5rem',
}

/**
 * Return the proper component for the icon.
 *
 * @param {Object|string} icon        the icon to be displayed
 * @param {string} defaultColor       color used if `icon` doesn't define it
 * @param {string} [size=medium]      size of the icon
 * @returns {JSX.Element|null}
 * @private
 */
const _getIcon = (icon, defaultColor, size = 'medium') => (
  <Icon
    source={icon}
    size={iconSizeMapping[icon?.size || size]}
    color={defaultColor}
  />
)

/**
 * A Button element with some options to configure it.
 *
 * @param {Object} forwardedRef             a reference to the root Button element
 * @param {Object} [events]                 an object containing the element's event handlers
 * @param {Function} [events.onClick]       an event handler function called whenever
 *                                          the button is clicked
 * @param {String} value                    the button's text TODO: rename to label
 * @param {Boolean} [fullWidth=true]        whether the button should take parent's whole width
 * @param {Object} [props]                  additional button's properties
 * @param {Object|String} [props.icon]      the name of an icon to be displayed before the label
 * @param {Object|String} [props.startIcon] the name of an icon to be displayed before the label
 * @param {Object|String} [props.endIcon]   the name of an icon to be displayed after the label
 *
 * @returns {JSX.Element}               a new simple button element
 */
const Button = ({ forwardedRef, events, value, fullWidth = true, ...props }) => {
  const {
    icon,
    startIcon = icon,
    endIcon,
    bold = true,
    size = 'medium',
    refName,
    classes,
    ...buttonProps
  } = props
  const { onClick } = events || {}
  const theme = useTheme()
  const color = theme.palette[buttonProps.color] || {}
  const contained = buttonProps.variant === 'contained'
  const hasIcon = icon || startIcon || endIcon

  const handleClick = useCallback((event) => {
    onClick?.(new PlatformEvent(event))
  }, [onClick])

  const horizontalPadding = _getButtonPadding(theme, size)
  const buttonHeight = _getButtonSize(theme, size)

  const iconDefaultColor = contained ? color.contrastText : color.main || 'text.primary'

  return (
    <MuiButton
      ref={forwardedRef}
      href={null}
      sx={{
        lineHeight: 'inherit',
        fontWeight: bold ? 700 : 400,
        boxShadow: 'none',
        height: buttonHeight,
        minHeight: buttonHeight,
        ...hasIcon && {
          paddingTop: horizontalPadding,
          paddingBottom: horizontalPadding,
          '& > .MuiButton-startIcon > div': {
            // backgroundColor: contained ? color.contrastText : color.main,
          },
          ...!value && {
            // if we only display an icon and no value, we need to overwrite
            // MUI's padding and minWidth
            padding: `${horizontalPadding}!important`,
            minWidth: _getButtonSize(theme, size),
            '& > .MuiButton-startIcon': {
              margin: 0,
            },
          },
        },
        ...contained && {
          ...color.contrastText && {
            color: `${color.contrastText}!important`,
          },
          ...color.dark && {
            '&:hover': {
              backgroundColor: `${color.dark}!important`,
            },
          },
          '&[disabled]': {
            opacity: 0.6,
            ...color.main && {
              backgroundColor: `${color.main}!important`,
            },
          },
        },
        ...buttonProps.style || {},
      }}
      startIcon={startIcon && _getIcon(startIcon, iconDefaultColor)}
      endIcon={endIcon && _getIcon(endIcon, iconDefaultColor)}
      onClick={handleClick}
      fullWidth={fullWidth}
      disableElevation={true}
      {...buttonProps}
    >
      {value}
    </MuiButton>
  )
}

Button.displayName = 'Button'

export default useMemoRef(OfflineHOC(Button), props => [
  props.value,
  props.disabled,
  props.events,
  props.icon,
  props.startIcon,
  props.endIcon,
])
